import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dumbbell, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { getWorkoutImage } from "@/utils/workoutImages";

const PAGE_SIZE = 6;

const difficultyColors = {
  beginner: "bg-emerald-500/10 text-emerald-500",
  intermediate: "bg-amber-500/10 text-amber-500",
  advanced: "bg-destructive/10 text-destructive",
};

const goalLabels = {
  strength: "Strength",
  hypertrophy: "Hypertrophy",
  endurance: "Endurance",
  fat_loss: "Fat Loss",
  general_fitness: "General Fitness",
};

export default function TemplatePickerDialog({ open, onOpenChange, onSelect }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["workout-templates"],
    queryFn: () => base44.entities.WorkoutTemplate.list("-created_date"),
    enabled: open,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(t =>
      t.name?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      goalLabels[t.goal]?.toLowerCase().includes(q) ||
      t.difficulty?.toLowerCase().includes(q)
    );
  }, [templates, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display">Choose a Template</DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search templates…"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1 space-y-3 pr-1 min-h-0">
          {isLoading && (
            <div className="text-center py-12 text-muted-foreground">Loading templates…</div>
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="text-center py-12">
              <Dumbbell className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">
                {search ? "No templates match your search" : "No templates available yet"}
              </p>
            </div>
          )}
          {paginated.map((t) => (
            <div
              key={t.id}
              className="rounded-xl border p-3 hover:bg-muted/40 transition-colors cursor-pointer flex items-center gap-3.5"
              onClick={() => onSelect(t)}
            >
              <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-slate-900 relative">
                <img
                  src={getWorkoutImage(t)}
                  alt={t.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold font-display truncate">{t.name}</p>
                {t.description && (
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{t.description}</p>
                )}
                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                  {t.difficulty && (
                    <Badge className={`text-[10px] ${difficultyColors[t.difficulty]}`}>{t.difficulty}</Badge>
                  )}
                  {t.goal && <Badge variant="outline" className="text-[10px]">{goalLabels[t.goal]}</Badge>}
                  {t.days_per_week && (
                    <Badge variant="secondary" className="text-[10px]">{t.days_per_week}d/week</Badge>
                  )}
                </div>
                {(t.exercises || []).length > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {(t.exercises || []).length} exercises
                  </p>
                )}
              </div>
              <Button size="sm" variant="secondary" className="shrink-0">
                Use
              </Button>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                disabled={currentPage === 1}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm px-2">{currentPage} / {totalPages}</span>
              <Button
                variant="ghost"
                size="icon"
                disabled={currentPage === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}